/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type AdminAccountPermissions,
  type AdminAccountSummary,
  adminActivityActions,
  adminActivityAnalytics,
  adminCreateAccount,
  adminCreateContact,
  adminDeleteUser,
  adminGetAccountPermissions,
  adminGetMcpConnectionInfo,
  adminGetUser,
  adminInviteUser,
  adminListAccounts,
  adminListActivity,
  adminListMcpAudit,
  adminListUsers,
  adminMcpAnalytics,
  adminMcpToolNames,
  type AdminPermissionUpdate,
  adminSetAccountMcp,
  adminSetAccountPermission,
  adminUpdateAccount,
  adminUpdateContact,
  adminUpdateUser,
  // Customer self-service portal (JJFW-840). These SDK symbols are generated
  // from the backend's new /customer/* routes; imports resolve once the
  // OpenAPI client is regenerated.
  customerApproveOrder,
  customerGetPermissions,
  customerInviteColleague,
  customerListPendingOrders,
  type CustomerPermissionUpdate,
  customerRejectOrder,
  customerSetColleagueActive,
  customerSetPermission,
  getActivity,
  getHealth,
  getMe,
  getMeDetail,
  logout,
  updateProfile,
} from "@/lib/api/generated";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401 — ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

// ─── Profile ─────────────────────────────────────────────────────────────

export function useMeDetail() {
  return useQuery({
    queryKey: ["me", "detail"],
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data;
    },
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: ["activity"],
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data } = await adminListUsers();
      return data ?? [];
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data;
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName?: string;
      lastName?: string;
      role?: string;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: {
          first_name: vars.firstName,
          last_name: vars.lastName,
          role: vars.role,
        },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; role?: string }) => {
      const { data } = await adminInviteUser({ body: { email: vars.email, role: vars.role } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Admin activity ──────────────────────────────────────────────────────

export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: ["admin", "activity", "analytics", days],
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: ["admin", "activity", "actions"],
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}

// ─── Admin accounts (MCP access) ───────────────────────────────────────────

const adminAccountsKey = ["admin", "accounts"] as const;
const setAccountMcpKey = ["admin", "accounts", "set-mcp"] as const;

export function useAdminAccounts() {
  return useQuery({
    queryKey: adminAccountsKey,
    queryFn: async () => {
      const { data } = await adminListAccounts();
      return data ?? [];
    },
  });
}

export function useSetAccountMcp() {
  const queryClient = useQueryClient();
  return useMutation({
    // A stable mutationKey lets onSettled defer the list refetch until the
    // last in-flight toggle finishes, so an early refetch can't clobber a
    // different row's still-pending optimistic update.
    mutationKey: setAccountMcpKey,
    mutationFn: async (vars: { accountId: string; enabled: boolean }) => {
      await adminSetAccountMcp({
        path: { account_id: vars.accountId },
        body: { enabled: vars.enabled },
      });
    },
    // Optimistically flip just this row so the switch responds instantly.
    // Snapshot only its previous value so a rollback restores this row alone
    // and leaves other rows' concurrent optimistic updates intact.
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: adminAccountsKey });
      const previousEnabled = queryClient
        .getQueryData<AdminAccountSummary[]>(adminAccountsKey)
        ?.find((account) => account.account_id === vars.accountId)?.mcp_enabled;
      queryClient.setQueryData<AdminAccountSummary[]>(adminAccountsKey, (accounts) =>
        accounts?.map((account) =>
          account.account_id === vars.accountId
            ? { ...account, mcp_enabled: vars.enabled }
            : account,
        ),
      );
      return { previousEnabled };
    },
    onError: (_error, vars, context) => {
      const previousEnabled = context?.previousEnabled;
      if (previousEnabled === undefined) {
        return;
      }
      queryClient.setQueryData<AdminAccountSummary[]>(adminAccountsKey, (accounts) =>
        accounts?.map((account) =>
          account.account_id === vars.accountId
            ? { ...account, mcp_enabled: previousEnabled }
            : account,
        ),
      );
    },
    onSettled: () => {
      // Refetch only once no toggles remain in flight. This settling mutation
      // is still counted, so a count of 1 means it's the last one.
      if (queryClient.isMutating({ mutationKey: setAccountMcpKey }) === 1) {
        void queryClient.invalidateQueries({ queryKey: adminAccountsKey });
      }
    },
  });
}

// ─── Admin account permissions (per-user MCP capability grid) ──────────────

const accountPermissionsKey = (accountId: string) =>
  ["admin", "accounts", accountId, "permissions"] as const;

export function useAccountPermissions(accountId: string | undefined) {
  return useQuery({
    queryKey: ["admin", "accounts", accountId, "permissions"],
    queryFn: async () => {
      const { data } = await adminGetAccountPermissions({
        path: { account_id: accountId ?? "" },
      });
      return data;
    },
    enabled: !!accountId,
    // A query-level retry fully replaces the global one, so re-state the
    // 401/403 skip and add 404: an unknown account is deterministic, so retrying
    // only delays the not-found card. Transient failures still retry.
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status === 401 || status === 403 || status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useSetAccountPermission(accountId: string) {
  const queryClient = useQueryClient();
  const permissionsKey = accountPermissionsKey(accountId);
  // A stable per-account mutationKey lets onSettled defer the reconciling
  // refetch until the last in-flight cell toggle finishes, so an early refetch
  // can't clobber a different cell's still-pending update.
  const mutationKey = ["admin", "accounts", accountId, "set-permission"] as const;
  return useMutation({
    mutationKey,
    mutationFn: async (update: AdminPermissionUpdate) => {
      const { data } = await adminSetAccountPermission({
        path: { account_id: accountId },
        body: update,
      });
      return data;
    },
    onSuccess: (data) => {
      // The backend returns the fully re-resolved grid — changing an account
      // default recomputes every inheriting cell — so replace the cache wholesale.
      if (data) {
        queryClient.setQueryData<AdminAccountPermissions>(permissionsKey, data);
      }
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey }) === 1) {
        void queryClient.invalidateQueries({ queryKey: permissionsKey });
      }
    },
  });
}

// ─── Admin account CRUD (JJFW-761) ─────────────────────────────────────────

export function useCreateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { accountName: string; segment?: string; region?: string }) => {
      const { data } = await adminCreateAccount({
        body: {
          account_name: vars.accountName,
          segment: vars.segment || null,
          region: vars.region || null,
        },
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminAccountsKey });
    },
  });
}

export function useUpdateAccount(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      accountName?: string;
      segment?: string | null;
      region?: string | null;
    }) => {
      const { data } = await adminUpdateAccount({
        path: { account_id: accountId },
        body: {
          account_name: vars.accountName,
          segment: vars.segment,
          region: vars.region,
        },
      });
      return data;
    },
    onSuccess: () => {
      // The account name shows in both the list and the detail permissions
      // payload, so refresh both.
      void queryClient.invalidateQueries({ queryKey: adminAccountsKey });
      void queryClient.invalidateQueries({ queryKey: accountPermissionsKey(accountId) });
    },
  });
}

export function useCreateContact(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { fullName: string; email?: string | null; role?: string | null }) => {
      const { data } = await adminCreateContact({
        path: { account_id: accountId },
        body: {
          full_name: vars.fullName,
          email: vars.email,
          role: vars.role,
        },
      });
      return data;
    },
    onSuccess: (data) => {
      // The create response is the full re-resolved permissions payload (with the
      // new contact row) — replace the detail-page cache wholesale so the grid
      // reflects it immediately.
      if (data) {
        queryClient.setQueryData<AdminAccountPermissions>(accountPermissionsKey(accountId), data);
      }
    },
  });
}

export function useUpdateContact(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      contactId: string;
      fullName?: string;
      // Explicit null clears the field server-side; undefined leaves it as-is.
      email?: string | null;
      role?: string | null;
      active?: boolean;
    }) => {
      const { data } = await adminUpdateContact({
        path: { account_id: accountId, contact_id: vars.contactId },
        body: {
          full_name: vars.fullName,
          email: vars.email,
          role: vars.role,
          active: vars.active,
        },
      });
      return data;
    },
    onSuccess: (data) => {
      // Returns the full refreshed AdminAccountPermissions — replace wholesale.
      if (data) {
        queryClient.setQueryData<AdminAccountPermissions>(accountPermissionsKey(accountId), data);
      }
    },
  });
}

// ─── Admin MCP connection info (JJFW-761 connection kit) ────────────────────

export function useMcpConnectionInfo() {
  return useQuery({
    queryKey: ["admin", "mcp", "connection"],
    queryFn: async () => {
      const { data } = await adminGetMcpConnectionInfo();
      return data;
    },
  });
}

// ─── Customer self-service portal (JJFW-840) ────────────────────────────────
//
// Mirror the admin hooks above: the customer manages their OWN account, so
// these endpoints take no account_id — the backend derives it from the
// customer's session. The permissions payload has the same shape as the admin
// grid (AdminAccountPermissions), so the portal grid reuses those types.

const customerPermissionsKey = ["customer", "permissions"] as const;
const pendingOrdersKey = ["customer", "orders", "pending"] as const;

export function useCustomerPermissions() {
  return useQuery({
    queryKey: customerPermissionsKey,
    queryFn: async () => {
      const { data } = await customerGetPermissions();
      return data;
    },
    // A 401/403 is deterministic (not signed in / not a customer) — don't retry;
    // the route guard handles the redirect. Transient failures still retry.
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status === 401 || status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useSetCustomerPermission() {
  const queryClient = useQueryClient();
  // A stable mutationKey lets onSettled defer the reconciling refetch until the
  // last in-flight cell toggle finishes, mirroring the admin grid.
  const mutationKey = ["customer", "set-permission"] as const;
  return useMutation({
    mutationKey,
    mutationFn: async (update: CustomerPermissionUpdate) => {
      const { data } = await customerSetPermission({ body: update });
      return data;
    },
    onSuccess: (data) => {
      // The backend returns the fully re-resolved grid — replace wholesale, but
      // only when this is the last in-flight toggle: with several cell writes
      // racing, an out-of-order (older) response must not overwrite the cache
      // with stale state. The deferred invalidate below reconciles regardless.
      if (data && queryClient.isMutating({ mutationKey }) === 1) {
        queryClient.setQueryData(customerPermissionsKey, data);
      }
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey }) === 1) {
        void queryClient.invalidateQueries({ queryKey: customerPermissionsKey });
      }
    },
  });
}

export function useInviteColleague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { fullName: string; email: string; role?: string }) => {
      const { data } = await customerInviteColleague({
        body: {
          full_name: vars.fullName,
          email: vars.email,
          role: vars.role?.trim() || null,
        },
      });
      return data;
    },
    onSuccess: (data) => {
      // The backend returns the refreshed account-permissions payload (same
      // shape as customerGetPermissions) — seed the grid cache with it, then
      // reconcile with the server.
      if (data) {
        queryClient.setQueryData<AdminAccountPermissions>(customerPermissionsKey, data);
      }
      void queryClient.invalidateQueries({ queryKey: customerPermissionsKey });
    },
  });
}

export function useSetColleagueActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { contactId: string; active: boolean }) => {
      const { data } = await customerSetColleagueActive({
        path: { contact_id: vars.contactId },
        body: { active: vars.active },
      });
      return data;
    },
    onSuccess: (data) => {
      // Returns the refreshed account-permissions payload — replace wholesale,
      // then reconcile with the server (mirrors useInviteColleague).
      if (data) {
        queryClient.setQueryData<AdminAccountPermissions>(customerPermissionsKey, data);
      }
      void queryClient.invalidateQueries({ queryKey: customerPermissionsKey });
    },
  });
}

export function usePendingOrders() {
  return useQuery({
    queryKey: pendingOrdersKey,
    queryFn: async () => {
      const { data } = await customerListPendingOrders();
      return data ?? [];
    },
  });
}

export function useApproveOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { orderId: string }) => {
      await customerApproveOrder({ path: { order_id: vars.orderId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pendingOrdersKey });
    },
  });
}

export function useRejectOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { orderId: string; reason: string }) => {
      await customerRejectOrder({
        path: { order_id: vars.orderId },
        body: { reason: vars.reason },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pendingOrdersKey });
    },
  });
}

// ─── Admin MCP usage insights (JJFW-762) ────────────────────────────────────

export function useAdminMcpAnalytics(days = 30, accountId?: string) {
  return useQuery({
    queryKey: ["admin", "mcp", "analytics", days, accountId ?? null],
    queryFn: async () => {
      const { data } = await adminMcpAnalytics({
        query: { days, account_id: accountId },
      });
      return data;
    },
  });
}

export interface AdminMcpAuditParams {
  page?: number;
  pageSize?: number;
  accountId?: string;
  tool?: string;
  outcome?: "ok" | "denied" | "error";
  search?: string;
}

export function useAdminMcpAudit(params: AdminMcpAuditParams = {}) {
  return useQuery({
    queryKey: ["admin", "mcp", "audit", params],
    queryFn: async () => {
      const { data } = await adminListMcpAudit({
        query: {
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
          account_id: params.accountId,
          tool: params.tool,
          outcome: params.outcome,
          search: params.search,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the audit
    // table doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminMcpToolNames() {
  return useQuery({
    queryKey: ["admin", "mcp", "tools"],
    queryFn: async () => {
      const { data } = await adminMcpToolNames();
      return data ?? [];
    },
  });
}
